/**
 * <em-scroll-controls aria-controls="ID">
 *   <button data-em-scroll="prev">…</button>
 *   <button data-em-scroll="next">…</button>
 * </em-scroll-controls>
 *
 * Previous / next buttons for any horizontally scrollable list. Each click moves by the width of one
 * item (plus the column gap). Buttons are disabled when the list is close to an edge, and the list
 * receives the `is-scrollable` class while its content overflows.
 */

const EDGE_THRESHOLD = 100;

class EmScrollControls extends HTMLElement {
  connectedCallback() {
    this.list = document.getElementById(this.getAttribute('aria-controls') || '');
    if (!this.list) return;

    this.prevButton = this.querySelector('[data-em-scroll="prev"]');
    this.nextButton = this.querySelector('[data-em-scroll="next"]');
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.prevButton?.addEventListener('click', () => this.move(-1), { signal });
    this.nextButton?.addEventListener('click', () => this.move(1), { signal });
    this.list.addEventListener('scroll', () => this.refresh(), { signal, passive: true });

    this.resizeObserver = new ResizeObserver(() => this.refresh());
    this.resizeObserver.observe(this.list);
    this.refresh();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.resizeObserver?.disconnect();
  }

  get itemStep() {
    const item = Array.from(this.list.children).find((child) => !child.hidden && child.offsetWidth > 0);
    if (!item) return this.list.clientWidth;

    const gap = parseFloat(getComputedStyle(this.list).columnGap) || 0;
    return item.getBoundingClientRect().width + gap;
  }

  move(direction) {
    const isRtl = getComputedStyle(this.list).direction === 'rtl';
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.list.scrollBy({
      left: direction * this.itemStep * (isRtl ? -1 : 1),
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }

  refresh() {
    const overflow = this.list.scrollWidth - this.list.clientWidth;
    const position = Math.abs(this.list.scrollLeft);
    const scrollable = overflow > 1;

    this.list.classList.toggle('is-scrollable', scrollable);
    this.toggleAttribute('data-scrollable', scrollable);

    if (this.prevButton) this.prevButton.disabled = !scrollable || position < Math.min(EDGE_THRESHOLD, overflow / 2);
    if (this.nextButton) this.nextButton.disabled = !scrollable || overflow - position < Math.min(EDGE_THRESHOLD, overflow / 2);
  }
}

if (!customElements.get('em-scroll-controls')) {
  customElements.define('em-scroll-controls', EmScrollControls);
}
